/**
 * The strip that says this is a recording.
 *
 * On every surface, not only on the start page. A demonstration that announces
 * itself once and then looks exactly like the real thing is the demonstration
 * somebody screenshots into a board pack, and the one place a prototype can do
 * real damage is in being mistaken for a deployment.
 *
 * It also carries the local change count, because a decision recorded in this
 * browser looks identical to one the platform took, and the difference is the
 * entire point.
 */
import { useEffect, useState } from 'react'

import { clearLocal, writes } from '../api/static'
import type { LocalWrite } from '../api/static'
import { Drawer } from './Drawer'

export function StaticBanner() {
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState<LocalWrite[]>([])

  useEffect(() => {
    // The strip is fixed above a header that is also fixed, so the layout has
    // to make room for it. The class carries that to the stylesheet rather
    // than relying on :has, which not every engine in use has.
    document.body.classList.add('fp-static')
    setLocal(writes())
    // The count has to follow an action taken on another surface, and an
    // action is a localStorage write rather than a React state change, so a
    // poll is the honest mechanism. Two seconds is below noticing.
    const timer = window.setInterval(() => setLocal(writes()), 2000)
    return () => {
      window.clearInterval(timer)
      document.body.classList.remove('fp-static')
    }
  }, [])

  const reset = () => {
    clearLocal()
    // Back to the document itself, hash cleared. Not to "/", which would leave
    // a build served from a subdirectory pointing at the host root.
    window.location.assign(window.location.pathname)
  }

  return (
    <>
      <div className="static-strip">
        <b>Static build.</b>
        <span>
          A recording of the platform. Nothing is enforced here, nothing is
          saved anywhere, and anything you do stays in this browser.
        </span>
        <div className="grow" />
        <button type="button" onClick={() => setOpen(true)}>
          Local changes · {local.length}
        </button>
        <button type="button" onClick={reset}>Reset</button>
      </div>

      <Drawer title="What you have changed in this browser" open={open}
              onClose={() => setOpen(false)}>
        <div className="ex-body">
          <h4>Where these went</h4>
          <p>
            Nowhere. Each one is a row in this browser's local storage. The
            platform's own handlers would have checked the grant, applied the
            constraint, written the outbox event and left an audit row written
            by a trigger. None of that happened, because none of it is here.
          </p>

          {local.length === 0 ? (
            <p className="muted">Nothing yet.</p>
          ) : (
            <ul className="trail">
              {local.map((w, i) => (
                <li key={i} className="op-update">
                  <div className="when">{w.at.replace('T', ' ').slice(0, 19)}</div>
                  <div className="who">
                    {w.summary}
                    <span className="b muted">{w.role}</span>
                  </div>
                  <table className="diff">
                    <tbody>
                      <tr><th>action</th><td><code>{w.path}</code></td></tr>
                      {Object.entries(w.fields).map(([k, v]) => (
                        <tr key={k}>
                          <th>{k}</th>
                          <td><span className="new">{v}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </li>
              ))}
            </ul>
          )}

          <div className="cav" style={{ marginTop: 16 }}>
            <b>Reset clears all of it</b> along with the chosen role and
            language, and returns to the start. The recording itself is files in
            the build and is not touched.
          </div>
        </div>
      </Drawer>
    </>
  )
}
